import React, { useEffect, useRef, useState } from 'react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { Icon } from '@/components/ui/Icon';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import {
  copyPageUrl,
  getExternalBrowserHint,
  getInAppBrowserName,
  isInAppBrowser,
  tryOpenExternalBrowser,
} from '@/utils/inAppBrowser';

const GOOGLE_RENDER_CHECK_MS = 2800;

interface GoogleSignInBlockProps {
  onSuccess: (response: CredentialResponse) => void;
  onError?: () => void;
  /** continue_with sur login, signin_with sur profil */
  text?: 'continue_with' | 'signin_with';
  pending?: boolean;
  error?: string | null;
  clientConfigured?: boolean;
}

export function GoogleSignInBlock({
  onSuccess,
  onError,
  text = 'continue_with',
  pending = false,
  error = null,
  clientConfigured = true,
}: GoogleSignInBlockProps) {
  const colors = useTheme();
  const googleHostRef = useRef<HTMLDivElement>(null);
  const inAppName = getInAppBrowserName();
  const [forceFallback, setForceFallback] = useState(isInAppBrowser());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!clientConfigured || pending || forceFallback) return;

    const timer = window.setTimeout(() => {
      const host = googleHostRef.current;
      if (!host) {
        setForceFallback(true);
        return;
      }
      const iframe = host.querySelector('iframe');
      const visible = Boolean(iframe && iframe.offsetHeight > 10 && iframe.offsetWidth > 10);
      if (!visible) setForceFallback(true);
    }, GOOGLE_RENDER_CHECK_MS);

    return () => window.clearTimeout(timer);
  }, [clientConfigured, pending, forceFallback]);

  const handleCopy = async () => {
    const ok = await copyPageUrl();
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    }
  };

  if (!clientConfigured) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 12,
          backgroundColor: colors.background,
          border: `1px dashed ${colors.border}`,
        }}
      >
        <span style={{ fontSize: 12, color: colors.textSecondary, textAlign: 'center', display: 'block' }}>
          Connexion Google indisponible :<br />
          <code style={{ color: Colors.primary }}>VITE_GOOGLE_CLIENT_ID</code> n'est pas configuré.
        </span>
      </div>
    );
  }

  if (forceFallback) {
    const hint = getExternalBrowserHint(inAppName);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 12, width: '100%', maxWidth: 320 }}>
        <div
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: `${Colors.primary}12`,
            border: `1px solid ${Colors.primary}35`,
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Icon name="information-circle" size={22} color={Colors.primary} />
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: colors.text, lineHeight: 1.4 }}>
                {inAppName
                  ? `Connexion Google indisponible dans ${inAppName}`
                  : 'Connexion Google indisponible ici'}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: colors.textSecondary, lineHeight: 1.45 }}>
                Google bloque la connexion dans les navigateurs intégrés. {hint}
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => tryOpenExternalBrowser()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            height: 48,
            borderRadius: 24,
            border: 'none',
            backgroundColor: Colors.primary,
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          <Icon name="open-outline" size={20} color="#ffffff" />
          Ouvrir dans Safari / Chrome
        </button>

        <button
          type="button"
          onClick={() => void handleCopy()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            height: 44,
            borderRadius: 22,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.background,
            color: colors.text,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          <Icon name={copied ? 'checkmark-circle' : 'copy-outline'} size={18} color={copied ? Colors.accent.green : colors.textSecondary} />
          {copied ? 'Lien copié' : 'Copier le lien'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
      {pending ? (
        <div className="spinner" />
      ) : (
        <div ref={googleHostRef} style={{ minHeight: 44, display: 'flex', justifyContent: 'center', width: '100%' }}>
          <GoogleLogin
            onSuccess={onSuccess}
            onError={onError ?? (() => setForceFallback(true))}
            useOneTap={false}
            theme="filled_black"
            text={text}
            shape="pill"
            size="large"
          />
        </div>
      )}
      {error && (
        <span className="login-auth-error" style={{ fontSize: 12, color: '#ff6b6b', textAlign: 'center' }}>
          {error}
        </span>
      )}
    </div>
  );
}
